import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  ArrowLeft, 
  Users, 
  Clock, 
  ThumbsUp, 
  ThumbsDown, 
  MessageCircle, 
  Flag,
  TrendingUp,
  BarChart3
} from "lucide-react";

// Mock data for debate detail
const mockDebateDetail = {
  id: 1,
  title: "재택근무가 생산성에 도움이 될까?",
  description: "코로나19 이후 재택근무가 일반화되면서, 재택근무의 생산성에 대한 논의가 활발해지고 있습니다. 여러분의 생각은 어떠신가요?",
  category: "사회",
  author: "토론러123",
  createdAt: "2024-01-03",
  agreeCount: 1247,
  disagreeCount: 856,
  totalVotes: 2103,
  timeLeft: "2일 남음",
  round: 152,
  isHot: true,
  aiSummary: {
    agree: [
      "통근 시간 절약으로 업무 집중도 향상",
      "개인 맞춤형 업무 환경 조성 가능",
      "워라밸 개선으로 직무 만족도 증가"
    ],
    disagree: [
      "동료와의 소통 부족으로 협업 효율성 저하",
      "집중력 분산과 업무-생활 경계 모호",
      "회사 문화 형성과 팀워크 구축 어려움"
    ]
  }
};

// Mock comments data
const mockComments = [
  {
    id: 1,
    author: "직장인A",
    avatar: "",
    content: "재택근무 하면서 통근 시간이 없어져서 오히려 더 많은 일을 할 수 있게 되었어요. 집중도도 훨씬 좋고요!",
    side: "agree",
    likes: 23,
    createdAt: "2시간 전",
    isPopular: true
  },
  {
    id: 2,
    author: "팀장님",
    avatar: "",
    content: "팀원들과의 소통이 어려워져서 프로젝트 진행이 더뎌지는 경우가 많아요. 화상회의로는 한계가 있는 것 같습니다.",
    side: "disagree",
    likes: 18,
    createdAt: "3시간 전",
    isPopular: false
  },
  {
    id: 3,
    author: "개발자99",
    avatar: "",
    content: "개발자 입장에서는 재택근무가 훨씬 효율적이에요. 조용한 환경에서 코딩에 집중할 수 있어서 생산성이 2배는 늘었습니다.",
    side: "agree",
    likes: 31,
    createdAt: "5시간 전",
    isPopular: true
  },
  {
    id: 4,
    author: "HR담당자",
    avatar: "",
    content: "신입사원들의 적응이 어려워지고, 회사 문화를 전달하기가 힘들어졌어요. 장기적으로는 조직력에 문제가 생길 것 같습니다.",
    side: "disagree",
    likes: 15,
    createdAt: "1일 전",
    isPopular: false
  }
];

const DebateDetail = () => {
  const { id } = useParams();
  const [selectedSide, setSelectedSide] = useState<"agree" | "disagree" | null>(null);
  const [comment, setComment] = useState("");
  const [sortBy, setSortBy] = useState("popular");

  const debate = mockDebateDetail;
  const agreePercentage = Math.round((debate.agreeCount / debate.totalVotes) * 100);
  const disagreePercentage = 100 - agreePercentage;

  const handleVote = (side: "agree" | "disagree") => {
    setSelectedSide(side);
    // Here you would typically send the vote to your backend
  };

  const handleSubmitComment = () => {
    if (!comment.trim() || !selectedSide) return;
    
    // Here you would typically send the comment to your backend
    console.log("Submitting comment:", { comment, side: selectedSide });
    setComment("");
  };

  const agreeComments = mockComments.filter(c => c.side === "agree");
  const disagreeComments = mockComments.filter(c => c.side === "disagree");

  const sortComments = (comments: typeof mockComments) => {
    return [...comments].sort((a, b) => {
      if (sortBy === "popular") {
        return b.likes - a.likes;
      } else {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" asChild>
                <Link to="/" className="flex items-center space-x-2">
                  <ArrowLeft className="w-4 h-4" />
                  <span>돌아가기</span>
                </Link>
              </Button>
              
              <div className="h-6 w-px bg-gray-300" />
              
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">B</span>
                </div>
                <span className="text-xl font-bold gradient-text">BothSides</span>
              </Link>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button variant="outline">
                로그인
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Debate Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <Badge variant="secondary" className="category-tag">
              {debate.category}
            </Badge>
            {debate.isHot && (
              <Badge variant="destructive" className="bg-red-500">
                🔥 HOT
              </Badge>
            )}
            <span className="text-sm text-gray-500">#{debate.round} 라운드</span>
            <span className="text-sm text-gray-500">by {debate.author}</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{debate.title}</h1>
          <p className="text-lg text-gray-600 mb-6">{debate.description}</p>
          
          {/* Vote Stats */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Users className="w-5 h-5 text-gray-500" />
                    <span className="font-semibold">{debate.totalVotes.toLocaleString()}명 참여</span>
                  </div>
                  <div className="flex items-center space-x-2 text-orange-600">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">{debate.timeLeft}</span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{agreePercentage}%</div>
                  <div className="text-sm text-gray-600">찬성 ({debate.agreeCount.toLocaleString()}명)</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{disagreePercentage}%</div>
                  <div className="text-sm text-gray-600">반대 ({debate.disagreeCount.toLocaleString()}명)</div>
                </div>
              </div>
              
              <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden mb-6">
                <div 
                  className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-400 to-green-500"
                  style={{ width: `${agreePercentage}%` }}
                />
                <div 
                  className="absolute right-0 top-0 h-full bg-gradient-to-l from-red-400 to-red-500"
                  style={{ width: `${disagreePercentage}%` }}
                />
              </div>
              
              {/* Vote Buttons */}
              {!selectedSide ? (
                <div className="grid grid-cols-2 gap-4">
                  <Button 
                    onClick={() => handleVote("agree")}
                    className="vote-button vote-button-agree h-12"
                  >
                    <ThumbsUp className="w-5 h-5 mr-2" />
                    찬성
                  </Button>
                  <Button 
                    onClick={() => handleVote("disagree")}
                    className="vote-button vote-button-disagree h-12"
                  >
                    <ThumbsDown className="w-5 h-5 mr-2" />
                    반대
                  </Button>
                </div>
              ) : (
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-blue-700 font-medium">
                    {selectedSide === "agree" ? "찬성" : "반대"} 의견으로 투표하셨습니다!
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    이제 아래에서 의견을 작성해보세요.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* AI Summary */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5" />
              <span>AI 핵심 논거 요약</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="font-semibold text-green-600 flex items-center space-x-2">
                  <ThumbsUp className="w-4 h-4" />
                  <span>찬성 측 핵심 논거</span>
                </h4>
                <ul className="space-y-2">
                  {debate.aiSummary.agree.map((point, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span className="text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-red-600 flex items-center space-x-2">
                  <ThumbsDown className="w-4 h-4" />
                  <span>반대 측 핵심 논거</span>
                </h4>
                <ul className="space-y-2">
                  {debate.aiSummary.disagree.map((point, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span className="text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Comment Section */}
        {selectedSide && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>의견 작성</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Badge variant={selectedSide === "agree" ? "default" : "destructive"} className={selectedSide === "agree" ? "bg-green-500" : "bg-red-500"}>
                    {selectedSide === "agree" ? "찬성" : "반대"} 의견
                  </Badge>
                </div>
                <Textarea
                  placeholder="여러분의 의견을 자유롭게 작성해주세요..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="min-h-[100px]"
                />
                <div className="flex justify-end">
                  <Button 
                    onClick={handleSubmitComment}
                    disabled={!comment.trim()}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    의견 등록
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Comments Display */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">토론 의견</h2>
            <Tabs value={sortBy} onValueChange={setSortBy}>
              <TabsList>
                <TabsTrigger value="popular" className="flex items-center space-x-1">
                  <TrendingUp className="w-4 h-4" />
                  <span>인기순</span>
                </TabsTrigger>
                <TabsTrigger value="latest" className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>최신순</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Agree Comments */}
            <div>
              <h3 className="text-lg font-semibold text-green-600 mb-4 flex items-center space-x-2">
                <ThumbsUp className="w-5 h-5" />
                <span>찬성 의견 ({agreeComments.length})</span>
              </h3>
              <div className="space-y-4">
                {sortComments(agreeComments).map((comment) => (
                  <Card key={comment.id} className={`${comment.isPopular ? 'ring-2 ring-green-200' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-3">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={comment.avatar} />
                          <AvatarFallback>{comment.author[0]}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="font-medium text-sm">{comment.author}</span>
                            <span className="text-xs text-gray-500">{comment.createdAt}</span>
                            {comment.isPopular && (
                              <Badge variant="outline" className="text-xs">인기</Badge>
                            )}
                          </div>
                          <p className="text-sm mb-3">{comment.content}</p>
                          <div className="flex items-center justify-between">
                            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-green-600">
                              <ThumbsUp className="w-4 h-4 mr-1" />
                              {comment.likes}
                            </Button>
                            <Button variant="ghost" size="sm" className="text-gray-400 hover:text-red-500">
                              <Flag className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Disagree Comments */}
            <div>
              <h3 className="text-lg font-semibold text-red-600 mb-4 flex items-center space-x-2">
                <ThumbsDown className="w-5 h-5" />
                <span>반대 의견 ({disagreeComments.length})</span>
              </h3>
              <div className="space-y-4">
                {sortComments(disagreeComments).map((comment) => (
                  <Card key={comment.id} className={`${comment.isPopular ? 'ring-2 ring-red-200' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-3">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={comment.avatar} />
                          <AvatarFallback>{comment.author[0]}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="font-medium text-sm">{comment.author}</span>
                            <span className="text-xs text-gray-500">{comment.createdAt}</span>
                            {comment.isPopular && (
                              <Badge variant="outline" className="text-xs">인기</Badge>
                            )}
                          </div>
                          <p className="text-sm mb-3">{comment.content}</p>
                          <div className="flex items-center justify-between">
                            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-red-600">
                              <ThumbsUp className="w-4 h-4 mr-1" />
                              {comment.likes}
                            </Button>
                            <Button variant="ghost" size="sm" className="text-gray-400 hover:text-red-500">
                              <Flag className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DebateDetail;